import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, ArrowRight } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_91cde765-1b33-4546-9396-09cd569422c7/artifacts/lyfig2as_DA71ED27-476E-40DD-8089-0450A8336436.jpg";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* CTA Section */}
      <div className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center justify-between gap-8"
          >
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-2">
                Ready to <span className="gradient-text">Transform</span> Your IT?
              </h3>
              <p className="text-slate-400">Let's discuss how we can help your business grow.</p>
            </div>
            <Link
              to="/contact"
              className="group flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-300"
              data-testid="footer-cta"
            >
              Get Started
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <img 
              src={LOGO_URL} 
              alt="ZenithForge Technology" 
              className="h-12 w-auto mb-6"
            />
            <p className="text-slate-400 mb-6 leading-relaxed">
              Your trusted IT partner, delivering innovative solutions that empower SMEs to thrive in the digital age.
            </p>
            <div className="flex gap-4">
              <a href="#" className="p-2 rounded-full bg-slate-800 hover:bg-cyan-600 transition-colors" aria-label="LinkedIn">
                <Linkedin size={18} />
              </a>
              <a href="#" className="p-2 rounded-full bg-slate-800 hover:bg-cyan-600 transition-colors" aria-label="Twitter">
                <Twitter size={18} />
              </a>
              <a href="#" className="p-2 rounded-full bg-slate-800 hover:bg-cyan-600 transition-colors" aria-label="Facebook">
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {['Home', 'About', 'Services', 'Contact'].map((item) => (
                <li key={item}>
                  <Link 
                    to={item === 'Home' ? '/' : `/${item.toLowerCase()}`}
                    className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2"
                  >
                    <ArrowRight size={14} />
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Our Services</h4>
            <ul className="space-y-3">
              {['Managed IT Services', 'IT Consulting', 'Project Management', 'Cloud Solutions', 'Cybersecurity'].map((item) => (
                <li key={item}>
                  <Link 
                    to="/services"
                    className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2"
                  >
                    <ArrowRight size={14} />
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a href="mailto:kishen@zenithforgetechnology.com" className="flex items-start gap-3 text-slate-400 hover:text-cyan-400 transition-colors">
                  <Mail size={18} className="mt-1 flex-shrink-0" />
                  <span>kishen@zenithforgetechnology.com</span>
                </a>
              </li>
              <li>
                <a href="tel:+27720350706" className="flex items-start gap-3 text-slate-400 hover:text-cyan-400 transition-colors">
                  <Phone size={18} className="mt-1 flex-shrink-0" />
                  <span>+27 72 035 0706</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin size={18} className="mt-1 flex-shrink-0" />
                <span>South Africa</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
            <p>&copy; {currentYear} ZenithForge Technology. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-cyan-400 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-cyan-400 transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
